import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { 
  Activity, Footprints, Heart, Scale, Smartphone, 
  ChevronRight, Check, RefreshCw, AlertCircle, Loader2
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config/api';
import HealthPlugin from '../plugins/HealthPlugin';

const API = API_URL;

// Brand icons URLs
const APPLE_HEALTH_ICON = "https://customer-assets.emergentagent.com/job_57e2e740-274d-4480-9155-167f0e5dcf96/artifacts/7trupdf7_IMG_4981.png";
const GOOGLE_FIT_ICON = "https://customer-assets.emergentagent.com/job_57e2e740-274d-4480-9155-167f0e5dcf96/artifacts/diev1j5a_IMG_4980.png";

export default function HealthIntegration({ onClose }) {
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [healthStatus, setHealthStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [connecting, setConnecting] = useState(null); // 'apple_health' | 'google_fit' | null
  const [isNativeApp, setIsNativeApp] = useState(false);
  const [platform, setPlatform] = useState('web');

  useEffect(() => {
    checkPlatform();
    loadHealthStatus();
    // Auto-trigger sync when the modal opens (e.g. tapping the
    // "Health & Fitness" button from Profilo). Runs after a short
    // delay to let the platform/status checks resolve first.
    const autoSyncTimer = setTimeout(() => {
      if (HealthPlugin.isNative()) {
        handleSync();
      }
    }, 600);
    return () => clearTimeout(autoSyncTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPlatform = async () => {
    const native = HealthPlugin.isNative();
    const plat = HealthPlugin.getPlatform();
    setIsNativeApp(native);
    setPlatform(plat);
    
    if (native) {
      const availability = await HealthPlugin.isHealthDataAvailable();
      console.log('Health data availability:', availability);
    }
  };

  const loadHealthStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/health/status`, { withCredentials: true });
      setHealthStatus({
        ...res.data,
        is_native_app: HealthPlugin.isNative()
      });
    } catch (err) {
      console.error('Error loading health status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleConnect = async (provider) => {
    if (!isNativeApp) {
      return;
    }

    // Only allow connecting the correct provider for the platform
    if (platform === 'ios' && provider !== 'apple_health') return;
    if (platform === 'android' && provider !== 'google_fit') return;

    setConnecting(provider);
    setSyncResult(null);

    try {
      // Pre-check availability: tells us if the HealthKit native
      // framework is wired AT ALL. If `available: false` here, the
      // user's iOS app build is missing either the HealthKit
      // capability (Xcode → Signing & Capabilities → + HealthKit)
      // or the Info.plist privacy strings — we surface a clear
      // diagnostic instead of the generic "auth failed".
      const avail = await HealthPlugin.isHealthDataAvailable();
      if (!avail?.available) {
        const reason = avail?.reason || avail?.error || 'unavailable';
        setSyncResult({
          success: false,
          message: `${t('health.pluginNotAvailable')}\n[debug: ${reason}]`
        });
        return;
      }

      // Request authorization from native health API.
      // We ask for the FULL set of fitness types supported by the
      // @capgo plugin (steps, distance, calories, heartRate, weight)
      // rather than just steps+weight. This serves two purposes:
      //   1) makes Slimmy appear in iOS Settings → Privacy → Health
      //      → "Apps" right after the first dialog (Apple only adds
      //      apps that requested permission for >= 1 data type that
      //      can be backed by HealthKit categories)
      //   2) future-proofs the integration for when we add cardio /
      //      heart-rate / active-energy displays — the user won't
      //      have to re-authorize separately.
      const authResult = await HealthPlugin.requestAuthorization([
        'steps', 'distance', 'calories', 'heartRate', 'weight'
      ]);

      if (authResult.success) {
        // Update backend with connection status
        await axios.post(`${API}/api/health/connect`, {
          provider,
          connected: true,
          permissions: authResult.permissions
        }, { withCredentials: true });

        // Sync data immediately after connecting
        await handleSync();
        
        // Reload status
        await loadHealthStatus();
      } else {
        // Distinguish "plugin not implemented" (build issue) from
        // "user denied permissions" (Settings → Privacy → Health).
        const err = (authResult.error || '').toLowerCase();
        const isMissingPlugin =
          err.includes('not implemented') ||
          err.includes('unimplemented') ||
          err.includes('not available') ||
          err.includes('entitlement') ||
          err.includes('healthkit');
        setSyncResult({
          success: false,
          message: isMissingPlugin
            ? `${t('health.pluginNotAvailable')}\n[debug: ${authResult.error}]`
            : `${t('health.authFailed')}\n[debug: ${authResult.error || 'unknown'}]`
        });
      }
    } catch (err) {
      console.error('Connect error:', err);
      const msg = (err?.message || '').toLowerCase();
      const isMissingPlugin =
        msg.includes('not implemented') ||
        msg.includes('unimplemented') ||
        msg.includes('not available') ||
        msg.includes('entitlement') ||
        msg.includes('healthkit');
      setSyncResult({
        success: false,
        message: isMissingPlugin
          ? `${t('health.pluginNotAvailable')}\n[debug: ${err.message}]`
          : `${err.message || t('health.authFailed')}`
      });
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider) => {
    try {
      await axios.post(`${API}/api/health/disconnect?provider=${provider}`, {}, { withCredentials: true });
      await loadHealthStatus();
    } catch (err) {
      console.error('Disconnect error:', err);
    }
  };

  const handleSync = async () => {
    if (!isNativeApp) return;
    
    setSyncing(true);
    setSyncResult(null);

    try {
      const result = await HealthPlugin.syncHealthDataToBackend(API, 7);
      
      if (result.success) {
        const totalSamples = (result.synced.steps || 0) + (result.synced.weights || 0);
        const readSamples = (result.readCounts?.steps || 0) + (result.readCounts?.weights || 0);
        // 3 possible states:
        //  (a) read 0 from HealthKit AND saved 0 → user missed read
        //      permissions in the iOS dialog. Show actionable error.
        //  (b) read N from HealthKit BUT saved 0 → POST failed (auth
        //      cookie missing, server error). Surface the raw error.
        //  (c) read N, saved N → real success.
        if (totalSamples === 0 && readSamples === 0 && platform === 'ios') {
          setSyncResult({
            success: false,
            message: t('health.readPermissionMissing'),
            actionable: true,
          });
        } else if (totalSamples === 0 && readSamples > 0) {
          // We DID read data from HealthKit, but the upload to backend
          // failed. The `errors` object explains why (HTTP code, etc).
          const errDetail = result.errors ? JSON.stringify(result.errors, null, 2) : 'unknown';
          setSyncResult({
            success: false,
            message: `Letti ${readSamples} dati da HealthKit ma il caricamento sul server è fallito.\n[debug: ${errDetail}]`,
          });
        } else {
          setSyncResult({
            success: true,
            message: t('health.synced') || 'Sincronizzato'
          });
        }
        await loadHealthStatus();
      } else {
        setSyncResult({
          success: false,
          message: result.error || 'Sync failed'
        });
      }
    } catch (err) {
      console.error('Sync error:', err);
      setSyncResult({
        success: false,
        message: err.message
      });
    } finally {
      setSyncing(false);
    }
  };

  /**
   * iOS-only deep link that takes the user straight into the Apple
   * Salute app, where they can navigate to:
   *   Profilo → Privacy → App → Slimmy → toggle READ permissions ON
   * This is the only way (Apple's design) to recover from a partial
   * authorization where the user skipped the read toggles.
   */
  const openHealthApp = () => {
    try {
      // x-apple-health:// opens the Health app on iOS. Doesn't deep-
      // link straight into Slimmy's section (Apple doesn't expose
      // that URL scheme), but it brings the user one tap away.
      window.location.href = 'x-apple-health://';
    } catch (e) {
      console.warn('Cannot open Apple Health', e);
    }
  };

  const formatSteps = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count?.toString() || '0';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t('health.never');
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Ora';
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h fa`;
    return date.toLocaleDateString();
  };

  const stepGoal = 10000;
  const todaySteps = healthStatus?.today_steps || 0;
  const stepProgress = Math.min((todaySteps / stepGoal) * 100, 100);

  // Determine which provider to show based on platform
  const showAppleHealth = platform === 'ios' || platform === 'web';
  const showGoogleFit = platform === 'android' || platform === 'web';

  if (loading) {
    return (
      <div className={`rounded-2xl p-6 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-2 border-[#E8334A] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`px-4 py-3 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
            <Activity className="w-5 h-5 text-green-500" />
          </div>
          <div className="flex-1">
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('health.title')}
            </h3>
            <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {t('health.subtitle')}
            </p>
          </div>
          {isNativeApp && (healthStatus?.apple_health?.connected || healthStatus?.google_fit?.connected) && (
            <button
              onClick={handleSync}
              disabled={syncing}
              className={`p-2 rounded-lg ${isDark ? 'bg-white/10' : 'bg-gray-100'} ${syncing ? 'opacity-50' : ''}`}
            >
              <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''} ${isDark ? 'text-white/70' : 'text-gray-600'}`} />
            </button>
          )}
        </div>
      </div>

      {/* Sync Result Message */}
      {syncResult && (
        <div className={`mx-4 mt-3 p-3 rounded-xl ${
          syncResult.success 
            ? isDark ? 'bg-green-500/20 text-green-300' : 'bg-green-50 text-green-700'
            : isDark ? 'bg-red-500/20 text-red-300' : 'bg-red-50 text-red-700'
        }`}>
          <div className="flex items-start gap-2">
            {syncResult.success ? (
              <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            )}
            <span className="text-xs whitespace-pre-wrap">{syncResult.message}</span>
          </div>
          {syncResult.actionable && platform === 'ios' && (
            <button
              onClick={openHealthApp}
              className={`mt-2 ml-6 text-xs underline font-medium ${isDark ? 'text-red-200' : 'text-red-700'}`}
              data-testid="open-health-app-btn"
            >
              {t('health.openHealthApp')}
            </button>
          )}
        </div>
      )}

      {/* Today's Steps */}
      <div className="px-4 py-3">
        <div className={`rounded-xl p-3 ${isDark ? 'bg-white/5' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Footprints className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
              <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('health.stepsToday')}
              </span>
            </div>
            <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {formatSteps(todaySteps)}
            </span>
          </div>
          
          <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500"
              style={{ width: `${stepProgress}%` }}
            />
          </div>
          <div className="flex justify-end mt-1">
            <span className={`text-[10px] ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
              {t('health.goal')}: {formatSteps(stepGoal)}
            </span>
          </div>
        </div>
      </div>

      {/* Recent Steps History */}
      {healthStatus?.recent_steps?.length > 0 && (
        <div className="px-4 pb-3">
          <h4 className={`text-xs font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
            Ultimi 7 giorni
          </h4>
          <div className="flex gap-1">
            {healthStatus.recent_steps.slice(0, 7).reverse().map((day, i) => {
              const height = Math.max(10, (day.count / stepGoal) * 100);
              return (
                <div key={day.date || `step-${i}`} className="flex-1 flex flex-col items-center gap-1">
                  <div 
                    className={`w-full rounded-t ${isDark ? 'bg-blue-400/30' : 'bg-blue-200'}`}
                    style={{ height: `${Math.min(height, 100)}%`, minHeight: '4px', maxHeight: '40px' }}
                  />
                  <span className={`text-[8px] ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                    {new Date(day.date).toLocaleDateString('it', { weekday: 'narrow' })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Providers */}
      <div className="px-4 pb-3 space-y-2">
        <h4 className={`text-xs font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
          {t('health.connectData')}
        </h4>

        {/* Apple Health */}
        {showAppleHealth && (
          <button
            onClick={() => healthStatus?.apple_health?.connected ? handleDisconnect('apple_health') : handleConnect('apple_health')}
            disabled={connecting === 'apple_health' || (!isNativeApp && !healthStatus?.apple_health?.connected)}
            className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${
              healthStatus?.apple_health?.connected
                ? isDark ? 'bg-green-500/20 border-green-500/50' : 'bg-green-50 border-green-200'
                : isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'
            } ${!isNativeApp && !healthStatus?.apple_health?.connected ? 'opacity-50' : ''}`}
          >
            <div className="flex items-center gap-3">
              <img 
                src={APPLE_HEALTH_ICON} 
                alt="Apple Health" 
                className="w-9 h-9 rounded-xl object-cover"
              />
              <div className="text-left">
                <span className={`font-medium text-sm block ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Apple Health
                </span>
                <span className={`text-[10px] ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  {healthStatus?.apple_health?.connected 
                    ? `${t('health.synced')} • ${formatDate(healthStatus?.apple_health?.last_sync)}`
                    : t('health.appleHealthDesc')
                  }
                </span>
              </div>
            </div>
            {connecting === 'apple_health' ? (
              <Loader2 className="w-5 h-5 text-[#E8334A] animate-spin" />
            ) : healthStatus?.apple_health?.connected ? (
              <Check className="w-5 h-5 text-green-500" />
            ) : (
              <ChevronRight className={`w-5 h-5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
            )}
          </button>
        )}

        {/* Google Fit */}
        {showGoogleFit && (
          <button
            onClick={() => healthStatus?.google_fit?.connected ? handleDisconnect('google_fit') : handleConnect('google_fit')}
            disabled={connecting === 'google_fit' || (!isNativeApp && !healthStatus?.google_fit?.connected)}
            className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${
              healthStatus?.google_fit?.connected
                ? isDark ? 'bg-green-500/20 border-green-500/50' : 'bg-green-50 border-green-200'
                : isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'
            } ${!isNativeApp && !healthStatus?.google_fit?.connected ? 'opacity-50' : ''}`}
          >
            <div className="flex items-center gap-3">
              <img 
                src={GOOGLE_FIT_ICON} 
                alt="Google Fit" 
                className="w-9 h-9 rounded-xl object-cover"
              />
              <div className="text-left">
                <span className={`font-medium text-sm block ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Google Fit
                </span>
                <span className={`text-[10px] ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  {healthStatus?.google_fit?.connected 
                    ? `${t('health.synced')} • ${formatDate(healthStatus?.google_fit?.last_sync)}`
                    : t('health.googleFitDesc')
                  }
                </span>
              </div>
            </div>
            {connecting === 'google_fit' ? (
              <Loader2 className="w-5 h-5 text-[#E8334A] animate-spin" />
            ) : healthStatus?.google_fit?.connected ? (
              <Check className="w-5 h-5 text-green-500" />
            ) : (
              <ChevronRight className={`w-5 h-5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
            )}
          </button>
        )}
      </div>

      {/* Coming Soon Notice - Only show on web */}
      {!isNativeApp && (
        <div className="px-4 pb-3">
          <div className={`flex items-center gap-3 p-3 rounded-xl ${isDark ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'}`}>
            <Smartphone className={`w-7 h-7 flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
            <div className="flex-1">
              <p className={`text-xs font-medium ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
                {t('health.comingSoon')}
              </p>
              <p className={`text-[10px] leading-tight ${isDark ? 'text-amber-200/70' : 'text-amber-700'}`}>
                {t('health.comingSoonText')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Syncable Data */}
      <div className={`px-4 pb-8 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} pt-3`}>
        <h4 className={`text-xs font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
          {t('health.syncableData')}
        </h4>
        <div className="grid grid-cols-4 gap-2">
          {[
            { icon: Scale, label: t('health.bodyWeight'), color: 'text-blue-500' },
            { icon: Footprints, label: t('health.dailySteps'), color: 'text-green-500' },
            { icon: Activity, label: t('health.workouts'), color: 'text-orange-500' },
            { icon: Heart, label: t('health.activeCalories'), color: 'text-red-500' },
          ].map((item, i) => (
            <div 
              key={item.label || `health-${i}`}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}
            >
              <item.icon className={`w-4 h-4 ${item.color}`} />
              <span className={`text-[9px] text-center leading-tight ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
